// FLOATING CHAT BUTTON - frontend/src/components/dashboard/FloatingChatButton.tsx
import React, { useState, useCallback } from 'react';
import { ChatBubbleLeftRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import ChatSection from './sections/ChatSection';
import styles from './FloatingChatButton.module.css';

interface FloatingChatButtonProps {
  className?: string;
}

const FloatingChatButton: React.FC<FloatingChatButtonProps> = ({ className = '' }) => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [hasNewMessage, setHasNewMessage] = useState(false);

  // Toggle chat modal
  const handleToggleChat = useCallback(() => {
    setIsChatOpen(!isChatOpen);
    if (!isChatOpen) {
      setHasNewMessage(false); // Clear notification when opening
    }
  }, [isChatOpen]);

  // Close chat modal
  const handleCloseChat = useCallback(() => {
    setIsChatOpen(false);
  }, []);

  // Handle chat minimize from within the chat component
  const handleChatMinimize = useCallback((minimized: boolean) => {
    if (minimized) {
      setIsChatOpen(false);
    }
  }, []);

  return (
    <>
      {/* Floating Chat Button */}
      <div className={`${styles.floatingChatButton} ${className}`}>
        <button
          className={`${styles.chatButton} ${isChatOpen ? styles.active : ''}`}
          onClick={handleToggleChat}
          aria-label={isChatOpen ? "Close PTS Assistant" : "Open PTS Assistant"}
          title={isChatOpen ? "Close PTS Assistant" : "Ask me about employees"}
        >
          {/* Human Icon Image */}
          <div className={styles.buttonIcon}>
            {isChatOpen ? (
              <XMarkIcon className={styles.closeIcon} />
            ) : (
              <img 
                src="/images/humanicon.png" 
                alt="PTS Assistant" 
                className={styles.humanIcon}
                onError={(e) => {
                  // Fallback to chat icon if image fails to load
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  target.nextElementSibling?.classList.remove(styles.hidden);
                }}
              />
            )}
            <ChatBubbleLeftRightIcon className={`${styles.fallbackIcon} ${styles.hidden}`} />
          </div>

          {/* Notification Badge */}
          {hasNewMessage && !isChatOpen && (
            <div className={styles.notificationBadge}>
              <span className={styles.badgeText}>!</span>
            </div>
          )}

          {/* Pulse Animation Ring */}
          <div className={styles.pulseRing}></div>
        </button>

        {/* Chat Caption */}
        {!isChatOpen && (
          <div className={styles.chatCaption}>
            <div className={styles.captionBubble}>
              <span className={styles.captionText}>Hey! Ask me👋</span>
              <div className={styles.captionArrow}></div>
            </div>
          </div>
        )}
      </div>

      {/* Chat Modal Overlay */}
      {isChatOpen && (
        <div className={styles.chatModalOverlay} onClick={handleCloseChat}>
          <div 
            className={styles.chatModalContainer} 
            onClick={(e) => e.stopPropagation()}
          >
            <ChatSection
              className={styles.modalChatSection}
              isMinimized={false}
              onToggleMinimize={handleChatMinimize}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default React.memo(FloatingChatButton);